import type {MonomerMapping} from 'bulk-import/panes/MonomerMappingForm';
import type {
  COLUMN_TYPE,
  DerivedColumn,
  ImportSourceType,
  IMPORT_TYPE,
  OPTIONS,
  Pane,
  PaneKey,
  SpreadsheetColumn,
  ValidateSpreadsheetForBulkImportResponse,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import type {FindDerivedColumnsResponse} from 'bulk-import/SpreadsheetBulkImportDAO';
import type {ImportSettings} from 'bulk-import/SpreadsheetBulkImportStore';
import type {EmptyPayload} from 'constants/events/EventPayloadUtils';
import type {SchemalessBulkImportSupportedOligoAwareTypes} from 'constants/RegistryConstants';
import type {Source} from 'constants/SourceConstants';

export interface SpreadsheetBulkImportFile {
  name: string;
}

export const SPREADSHEET_BULK_IMPORT_EVENT_PAYLOADS = {
  SPREADSHEET_BULK_IMPORT_BEGIN_IMPORT_FLOW: {} as {
    panes: Array<Pane>;
    importType: IMPORT_TYPE;
    initialRegistry?: Source | null;
    initialFolder?: {folderId: string | null; registryId?: string | null} | null;
    initialBatchSchemaId?: string | null;
    initialContainerSchemaId?: string | null;
    initialTagSchemaId?: string | null;
    initialSampleGroupSchemaId?: string | null;
    initialResultSchemaId?: string | null;
    initialProcessTaskSchemaId?: string | null;
    initialProcessOutputSchemaId?: string | null;
    initialTrialSubjectSchemaId?: string | null;
    initialPlateSchemaId?: string | null;
    initialBoxSchemaId?: string | null;
    initialTableColumnNames?: Array<string>;
    initialDataTableKeyFields?: Array<COLUMN_TYPE>;
    initialSchemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes;
    initialRawText?: string;
    initialPaneKey?: PaneKey;
  },
  SPREADSHEET_BULK_IMPORT_COMPLETED: {} as EmptyPayload,
  SPREADSHEET_BULK_IMPORT_FAILED: {} as EmptyPayload,
  SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS: {} as EmptyPayload,
  SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS_COMPLETED: {} as {
    derivedColumnsResponse: FindDerivedColumnsResponse;
  },
  SPREADSHEET_BULK_IMPORT_FIND_DERIVED_COLUMNS_FAILED: {} as EmptyPayload,
  SPREADSHEET_BULK_IMPORT_GO_TO_NEXT_PANE: {} as EmptyPayload,
  SPREADSHEET_BULK_IMPORT_GO_TO_PREV_PANE: {} as EmptyPayload,
  SPREADSHEET_BULK_IMPORT_GO_TO_PANE_WITH_KEY: {} as {paneKey: PaneKey},
  SPREADSHEET_BULK_IMPORT_REMOVE_SPREADSHEET: {} as EmptyPayload,
  SPREADSHEET_BULK_IMPORT_RESET: {} as EmptyPayload,
  SPREADSHEET_BULK_IMPORT_SET_SPREADSHEET_COLUMNS_MAPPING_NAME: {} as {
    spreadsheetColumnsMappingName: string | null;
  },
  SPREADSHEET_BULK_IMPORT_SET_COLUMNS: {} as {columns: Array<SpreadsheetColumn>},
  SPREADSHEET_BULK_IMPORT_SET_DERIVED_COLUMNS: {} as {derivedColumns: Array<DerivedColumn>},
  SPREADSHEET_BULK_IMPORT_SET_OPTIONS: {} as {options: {[key in OPTIONS]?: boolean}},
  SPREADSHEET_BULK_IMPORT_SET_IMPORT_SETTING: {} as ImportSettings,
  SPREADSHEET_BULK_IMPORT_SET_LANGUAGE_TAG: {} as EmptyPayload,
  SPREADSHEET_BULK_IMPORT_SET_TIME_LIMITS: {} as {_timeLimit: number},
  SPREADSHEET_BULK_IMPORT_SOURCE_TYPE_CHANGED: {} as {value: ImportSourceType},
  SPREADSHEET_BULK_IMPORT_RAW_TEXT_UPDATED: {} as {rawText: string},
  SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE: {} as {file: SpreadsheetBulkImportFile},
  SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_COMPLETED: {} as {
    file: SpreadsheetBulkImportFile;
    url: string;
    columnNames: Array<string>;
    dataTypes?: Array<string>;
  },
  SPREADSHEET_BULK_IMPORT_UPLOAD_AND_PARSE_FAILED: {} as {
    errorMessage: string | null;
    file: SpreadsheetBulkImportFile;
  },
  SPREADSHEET_BULK_IMPORT_VALIDATE: {} as EmptyPayload,
  SPREADSHEET_BULK_IMPORT_VALIDATE_COMPLETED: {} as ValidateSpreadsheetForBulkImportResponse & {url: string},
  SPREADSHEET_BULK_IMPORT_VALIDATE_FAILED: {} as {url: string; failureMessage?: string},
  SPREADSHEET_BULK_IMPORT_SET_SHOULD_CLOSE_ON_REQUEST: {} as {shouldCloseOnRequest: boolean},
  SPREADSHEET_BULK_IMPORT_UPDATE_MONOMER_MAPPING: {} as {monomerMapping: MonomerMapping},

  SPREADSHEET_BULK_TRANSFER_STORABLES_COMPLETED: {} as {invalidatableStorableIds: Array<string>},
} as const;

export type SpreadsheetBulkImportEventPayload = typeof SPREADSHEET_BULK_IMPORT_EVENT_PAYLOADS;
